import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatStepper } from '@angular/material';
import { WizardContactData, WizardCustomerData, WizardJobData } from '../../objects/wizard';
import { Observable, of, forkJoin } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { NotificationService } from '../../../../services/notification.service';
import { WizardService } from '../../services/wizard.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { get } from 'lodash-es';
import { WizardComponentInterface } from '../wizard/wizard.component.interface';
import { WizardStep, WizardStepper } from '../../objects/wizard-stepper';
import { CustomerComponent } from '../steps/customer/customer.component';
import { AssetService } from '../../../../services/asset.service';
import { LayoutDashboardService } from '../../../../shared/layouts/layout-dashboard/layout-dashboard.service';
import { blank, data_get, fallback, filled, isId } from '../../../../shared/utils/common';
import { LooseObject } from '../../../../objects/loose-object';
import { ContactComponent } from '../steps/contact/contact.component';
import { JobComponent } from '../steps/job/job.component';
import { ClientStoreService } from '../../../../services/client-store.service';
import { FileService } from '../../../../services/file/file.service';

@Component({
  selector: 'app-create-job-wizard',
  templateUrl: './create-job-wizard.component.html',
  styleUrls: ['./create-job-wizard.component.scss'],
  providers: [AssetService]
})
export class CreateJobWizardComponent implements OnInit, WizardComponentInterface {

  /**
   * The stepper initial settings. You can also update the
   * stepper through here but do note that it will have a delay as
   * change detection of child components take a while to reflect.
   *
   * @var {WizardStepper}
   */
  public wizardStepper: WizardStepper = new WizardStepper(
    new WizardStep({title: 'customer', icon: 'users', optional: false, editable: true, completed: false}),
    new WizardStep({title: 'contact', icon: 'address-book', optional: true, editable: true, completed: false}),
    new WizardStep({title: 'job', icon: 'wrench', optional: false, editable: true, completed: false}),
    new WizardStep({title: 'summary', icon: 'book', optional: false, editable: false, completed: false})
  );

  private _config: LooseObject = {};

  set config(value: LooseObject) {
    this._config = value;
  }

  get config(): LooseObject {
    return this._config;
  }

  @ViewChild(CustomerComponent) customer: CustomerComponent;
  @ViewChild(ContactComponent) contact: ContactComponent;
  @ViewChild(JobComponent) job: JobComponent;

  constructor(
    public dialogRef: MatDialogRef<CreateJobWizardComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private notifService: NotificationService,
    public wizardService: WizardService,
    private assetService: AssetService,
    private layoutDashboardService: LayoutDashboardService,
    private router: Router,
    private clientStoreService: ClientStoreService,
    private fileService: FileService
  ) {
    this.dialogData = this.wizardService.checkPreselectedData(layoutDashboardService, dialogData);
  }

  /**
   * Loading indicator.
   *
   * @var {boolean}
   */
  public bLoading: boolean = false;

  /**
   * The id of the customer if it already exists.
   *
   * @var {string}
   */
  public strCustomerId?: string;

  /**
   * The id of the site if it already exists.
   *
   * @var {string}
   */
  public strSiteId?: string;

  /**
   * Holds the data for this wizard.
   *
   * @var {CustomerWizardData}
   */
  public objWizard: {
    contact: WizardContactData | null,
    customer: WizardCustomerData | null,
    job: WizardJobData | null
  } = {
      contact: null,
      customer: null,
      job: null
    };

  /**
   * Checks if job has a customer.
   *
   * @var {boolean}
   */
  bHasNoCustomer: boolean = false;

  /**
   * Checks if job is at a custom location.
   *
   * @var {boolean}
   */
  bIsCustomLocation: boolean = false;

  /**
   * Identify which redirect to go to after creation.
   *
   * @var {string}
   */
  strRedirect?: 'create' | 'schedule' | 'work_order';

  /**
   * Checks if the customer step is finished.
   *
   * @var {boolean}
   */
  bCustomerStepFinished = false;

  extractedLineItems = [];

  objFiles = {};

  ngOnInit() {
    if (!this.dialogData.customer_id && !this.dialogData.opened_from_mega_menu) {
      this.changeCustomerStepName(true);
    }
  }

  captureJobConfig(config: LooseObject) {
    this._config = config;
  }

  /**
   * Get the updates from the contact form
   * and save it to this component.
   *
   * @param {WizardContactData} objContactData
   *
   * @returns {void}
   */
  captureContactData(objContactData: WizardContactData, stepper: MatStepper): void{
    this.objWizard.contact = objContactData;

    if (objContactData != null) {
      stepper.steps.find(item => item.state == 'step2').completed = true;
      stepper.next();
    }
  }

  /**
   * Captures the customer data
   *
   * @param {WizardCustomerData} objCustomerData
   * @param {MatStepper} objStepper
   *
   * @returns {void}
   */
  captureCustomerData(objCustomerData: any, stepper: MatStepper): void {
    this.objWizard.customer = objCustomerData;
    this.strCustomerId = null;
    this.strSiteId = null;

    if (get(objCustomerData, 'customer_id')) {
      this.strCustomerId = objCustomerData['customer_id'];
    }

    if (get(objCustomerData, 'site_id')) {
      this.strSiteId = objCustomerData['site_id'];
    }

    if (objCustomerData != null) {
      stepper.steps.first.completed = true;
      stepper.next();
    }

    this.bCustomerStepFinished = true;
  }

  /**
   * Captures the job data
   *
   * @param {WizardJobData} objJobData
   * @param {MatStepper} objStepper
   *
   * @returns {void}
   */
  captureJobData(objJobData: WizardJobData, objStepper: MatStepper): void {
    this.objWizard.job = objJobData;

    if (objJobData != null) {
      objStepper.steps.find(item => item.state == 'step3').completed = true;
      objStepper.next();
    }

  }

  /**
   * Actions to perform if the customer step is skipped.
   *
   * @param {boolean} bFlag
   * @param {MatStepper} stepper
   *
   * @returns {void}
   */
  skipCustomerStep(bFlag: boolean, stepper: MatStepper): void {
    if (bFlag) {
      stepper.steps.first.completed = true;
      stepper.next();
      this.bCustomerStepFinished = true;
    }
  }

  /**
   * Actions to perform if the contact step is skipped.
   *
   * @param {boolean} bFlag
   * @param {MatStepper} stepper
   *
   * @returns {void}
   */
  skipContactStep(bFlag: boolean, stepper: MatStepper): void {
    if (bFlag) {
      stepper.steps.find(item => item.state == 'step2').completed = true;
      stepper.next();
    }
  }

  /**
   * Schedule the job details.
   *
   * @returns {void}
   */
  save(): void{
    this.bLoading = true;
    this.setJobDefaultValues();
    this.wizardService.saveJobDetails(this.objWizard).pipe(
      switchMap((jobDetailsRes) =>
        forkJoin([
          this.getLinkedAssetObservable(jobDetailsRes.body.record_id),
          of(jobDetailsRes),
        ])
      ),
      switchMap(([, jobDetailsRes]) => {
        if (filled(this.objFiles)) {
          const uploadName = this.objFiles['upload_name'];
          let objRequestData = {
            id: uploadName.split('/')[1],
            file: this.objFiles,
            upload_name: uploadName,
            module_field: 'job_id',
            module: 'jobs',
            module_id: jobDetailsRes.body.record_id,
            description: ""
          }

          return forkJoin([
            this.fileService.save(objRequestData),
            of(jobDetailsRes)
          ])
        } else {
          return forkJoin([
            of(null),
            of(jobDetailsRes)
          ])
        }
      }),
      switchMap(([, jobDetailsRes]) => {
        let obs: Observable<{ answer: boolean }> = of({ answer: true });

        return forkJoin([
          obs,
          of(jobDetailsRes)
        ]);
      }))
      .subscribe(([dialogRes, jobDetailsRes]) => {
        this.bLoading = false;
        this.notifService.notifySuccess('job_successfully_created');
        this.dialogRef.close('save');
        if (get(dialogRes, 'answer') === true) {
          if (this.strRedirect == 'create') {
            this.layoutDashboardService.strActiveParent = 'jobs';
            this.router.navigate([`/jobs/${jobDetailsRes.body.record_id}`]);
          }

          if (this.strRedirect == 'work_order') {
            this.layoutDashboardService.strActiveParent = 'jobs';
            this.router.navigate([`/jobs/${jobDetailsRes.body.record_id}`], {queryParams: {work_order: 'open'}});
          }

          if (this.strRedirect == 'schedule') {
            this.router.navigate([`/jobs/${jobDetailsRes.body.record_id}`], {queryParams: {scheduler: 'open'}});
          }
        }
      }, (error: HttpErrorResponse) => {
        let errorIds = Object.keys(error.error);
        if (errorIds.length && errorIds[0]) {
          this.notifService.notifyWarning(error.error[errorIds[0]][0]);
        } else {
          this.notifService.notifyWarning('record_invalid_parameters');
        }
        this.bLoading = false;
      }, () => {
        this.bLoading = false;
      });
  }

  /**
   * Gets the observable to link the asset to the newly created job
   *
   * @param {string} strJobId
   *
   * @returns {Observable<any> }
   */
  getLinkedAssetObservable(strJobId: string): Observable<any> {
    const strAssetId: string = get(this.dialogData, 'asset_data.asset_id');
    const arJobAssets = get(this.objWizard.job, 'assets', []) || [];

    const arLinkedAssetIds = arJobAssets
      .map(item => get(item, 'id'))
      .filter(id => isId(id));

    if (isId(strAssetId) && !arLinkedAssetIds.includes(strAssetId)) {
      arLinkedAssetIds.push(strAssetId);
    }

    return filled(arLinkedAssetIds)
      ? this.assetService.linkAssets(new Set(arLinkedAssetIds), strJobId)
      : of([]);
  }

  /**
   * Checks if this stepper is dirty.
   *
   * @returns {boolean}
   */
  isDirty(): boolean {
    return this.customer.form.dirty
  }

  /**
   * Only visible in create job wizard. Changes the name of the step from 'Customer' to 'Location'
   * if the 'No customer' checkbox is checked.
   *
   * @param {boolean} bFlag
   *
   * @returns {void}
   */
  changeCustomerStepName(bFlag: boolean): void {
    this.bHasNoCustomer = bFlag;

    if (bFlag) {
      this.wizardStepper.step1 = new WizardStep({title: 'location', icon: 'map-marked-alt', optional: true, editable: true, completed: false});
    } else {
      this.wizardStepper.step1 = new WizardStep({title: 'customer', icon: 'users', optional: false, editable: true, completed: false});
    }
  }

  setJobDefaultValues(): void {
    if (filled(get(this.dialogData, 'contract_id'))) {
      this.objWizard.job.contract_id = this.dialogData.contract_id;
    }

    if (filled(this.extractedLineItems)) {
      this.objWizard.job.work_order_items = this.extractedLineItems;
    }
  }

  onUpload(event): void {
    if (blank(event)) {
      return;
    }

    this.wizardService.resetStepper.next();
    this.customer.fromExtractedPdf(event);
    this.contact.fromExtractedPdf(event);
    this.job.form.controls.job_summary.setValue(data_get(event, 'description'));

    if (filled(data_get(event, 'line_items'))) {
      const currentClientConfig = this.clientStoreService.getActiveClient();
      this.extractedLineItems = data_get(event, 'line_items');
      this.extractedLineItems.forEach(item => {
        item['markup'] = fallback(data_get(currentClientConfig['config'], 'default_markup'), {
          fallback: () => 0
        });
        item['enable_discounts'] = fallback(data_get(currentClientConfig['config'], 'enable_discounts'), {
          fallback: () => false
        });
        item['tax_code_id'] = data_get(currentClientConfig, 'default_tax_code_sale');
        item['unit_cost'] = parseFloat(item['unit_price'].substring(1));
        item['unit_price'] = item['unit_cost'] * (1 + (item['markup'] / 100));
        item['line_item'] = item['unit_price'];
      })
    }

    this.objFiles = data_get(event, 'objFiles');
  }
}